import {
  Grid,
  Box,
  Text,
  Divider,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  HStack,
  Button
} from '@chakra-ui/react'
import { FC, useCallback, useMemo, useState } from 'react'
import { TiLockClosed } from 'react-icons/ti'

import {
  ITableGridHeaderColumn,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutActionColumns } from '@base/@types/components/Layout/Tools'

import LayoutToolsButton from '../Components/Button'

const INTERNAL_COLUMN_IDS = ['indicadores-planej-pcl-id', 'id-id']

const cloneHeaders = (source: ITableHeader[]) =>
  source.map(group => ({
    ...group,
    columns: group.columns
      ? group.columns.map(col => ({ ...col }))
      : group.columns
  }))

const ActionColumns: FC<ILayoutActionColumns> = ({
  data,
  onChange,
  disableActionsByDefault
}) => {
  const [isOpen, setIsOpen] = useState(false)
  // Snapshot of the headers taken when the modal opens — edits are instant here
  // and only hit the table once, on apply/close.
  const [localHeaders, setLocalHeaders] = useState<ITableHeader[]>([])

  // Columns the user is not allowed to touch (internal ids, ignored columns and
  // the ones the screen locks by default).
  const isLocked = useCallback(
    (column: ITableGridHeaderColumn) =>
      INTERNAL_COLUMN_IDS.includes(column.id) ||
      !!column.ignore ||
      (!!disableActionsByDefault && disableActionsByDefault(column.id)),
    [disableActionsByDefault]
  )

  // Columns that are never listed in the editor at all.
  const isHiddenFromEditor = useCallback(
    (column: ITableGridHeaderColumn) =>
      INTERNAL_COLUMN_IDS.includes(column.id) || !!column.ignore,
    []
  )

  const onOpen = useCallback(() => {
    // Always re-read the live headers, so the editor mirrors exactly what the
    // grid (and the export) is using right now.
    setLocalHeaders(cloneHeaders(data as ITableHeader[]))
    setIsOpen(true)
  }, [data])

  // Diff between the snapshot being edited and the live headers. Deriving it
  // (instead of bookkeeping a Set of toggles) keeps the editor in sync even if
  // the headers change underneath, and drops back to empty once applied.
  const pendingToggles = useMemo(() => {
    const toggles: Array<[number, number]> = []

    localHeaders.forEach((group, groupIdx) => {
      if (!group.columns) return

      group.columns.forEach((column, columnIdx) => {
        const liveColumn = (data as ITableHeader[])[groupIdx]?.columns?.[
          columnIdx
        ]

        if (liveColumn && liveColumn.id === column.id) {
          if (liveColumn.show !== column.show) {
            toggles.push([groupIdx, columnIdx])
          }
        }
      })
    })

    return toggles
  }, [localHeaders, data])

  const applyChanges = useCallback(() => {
    // Single batch → one table re-render.
    pendingToggles.forEach(([groupIdx, columnIdx]) => {
      onChange(groupIdx, columnIdx)
    })
    setIsOpen(false)
  }, [pendingToggles, onChange])

  const handleToggle = useCallback((groupIdx: number, columnIdx: number) => {
    setLocalHeaders(prev =>
      prev.map((group, gIdx) => {
        if (gIdx !== groupIdx || !group.columns) return group

        return {
          ...group,
          columns: group.columns.map((col, cIdx) =>
            cIdx === columnIdx ? { ...col, show: !col.show } : col
          )
        }
      })
    )
  }, [])

  const setAll = useCallback(
    (show: boolean) => {
      setLocalHeaders(prev =>
        prev.map(group => {
          if (!group.columns) return group

          return {
            ...group,
            columns: group.columns.map(col =>
              isLocked(col) ? col : { ...col, show }
            )
          }
        })
      )
    },
    [isLocked]
  )

  const handleSelectAll = useCallback(() => setAll(true), [setAll])
  const handleDeselectAll = useCallback(() => setAll(false), [setAll])

  // A group is listed whenever it has at least one configurable column —
  // regardless of whether those columns are currently visible, otherwise a
  // group the user hid entirely could never be brought back.
  const hasConfigurableColumns = useCallback(
    (columns?: ITableGridHeaderColumn[]) =>
      !!columns && columns.some(column => !isHiddenFromEditor(column)),
    [isHiddenFromEditor]
  )

  return (
    <>
      <LayoutToolsButton label="Editar Colunas" onClick={onOpen} />

      {isOpen && (
        <Modal
          isCentered
          isOpen={isOpen}
          onClose={applyChanges}
          scrollBehavior="inside"
          motionPreset="slideInBottom"
        >
          <ModalOverlay />

          <ModalContent marginX={4} maxW={400}>
            <ModalHeader
              fontSize={16}
              paddingTop={2}
              paddingBottom={2}
              paddingLeft={4}
              paddingRight={4}
              textColor={'gray.200'}
              background={'#1d5840'}
              borderTopLeftRadius={5}
              borderTopRightRadius={5}
              borderBottom="3px solid #ECC94B"
            >
              Configurar colunas
            </ModalHeader>

            <ModalCloseButton color="white" marginTop={-1} marginRight={-1} />

            <ModalBody
              bgColor="white"
              borderBottomRadius={5}
              paddingX={4}
              paddingBottom={4}
              paddingTop={0}
            >
              <HStack spacing={6} mt={4} mb={3} justifyContent="center">
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="green.600"
                  cursor="pointer"
                  onClick={handleSelectAll}
                  _hover={{
                    color: 'green.500',
                    textDecoration: 'underline'
                  }}
                >
                  ✓ Selecionar tudo
                </Text>
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="red.600"
                  cursor="pointer"
                  onClick={handleDeselectAll}
                  _hover={{
                    color: 'red.500',
                    textDecoration: 'underline'
                  }}
                >
                  ✗ Desmarcar tudo
                </Text>
              </HStack>

              <Divider my={2} bgColor="gray.200" />

              {localHeaders.map((row: ITableHeader, gpIdx: number) => {
                if (!hasConfigurableColumns(row.columns)) return null

                return (
                  <Box key={`columns-group-${gpIdx}`} mt={4}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      color="gray.600"
                    >
                      {row.group}
                    </Text>

                    <Divider my={1} bgColor="gray.300" />

                    <Grid templateColumns="repeat(2, 1fr)" gap="4px">
                      {row.columns!.map(
                        (column: ITableGridHeaderColumn, columnIdx: number) => {
                          if (isHiddenFromEditor(column)) return null

                          const locked = isLocked(column)

                          return (
                            <Flex
                              alignItems="center"
                              key={`column-${gpIdx}-${columnIdx}`}
                            >
                              {locked && (
                                <Box marginRight={1}>
                                  <TiLockClosed color="#9f9f9f" />
                                </Box>
                              )}

                              <Checkbox
                                isChecked={column.show}
                                borderColor="blackAlpha.300"
                                onChange={() => handleToggle(gpIdx, columnIdx)}
                                colorScheme="green"
                                isDisabled={locked}
                                css={{
                                  '.css-7sn7zq[data-checked][data-disabled]': {
                                    borderColor: '#dcdcdc',
                                    color: 'lightGray'
                                  },
                                  '.css-7sn7zq[data-disabled]': {
                                    border: '2px solid #dcdcdc',
                                    color: 'lightGray'
                                  }
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: 600
                                  }}
                                >
                                  {column.name}
                                </span>
                              </Checkbox>
                            </Flex>
                          )
                        }
                      )}
                    </Grid>
                  </Box>
                )
              })}

              {pendingToggles.length > 0 && (
                <Button
                  mt={4}
                  w="100%"
                  size="sm"
                  colorScheme="green"
                  onClick={applyChanges}
                >
                  Aplicar ({pendingToggles.length}{' '}
                  {pendingToggles.length === 1 ? 'alteração' : 'alterações'})
                </Button>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

export default ActionColumns
